// RootLayout wraps all authenticated tenant pages.
// Layout: full-width header (100px) → [sidebar + [breadcrumb bar + content]] → full-width footer.

import { useState } from 'react'
import { Outlet, useRouterState } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { Sidebar } from '@/components/layout/Sidebar'
import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { ChevronRight, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useSubscriptionStatus } from '@/lib/hooks/use-subscription-status'
import { platformApi } from '@/lib/api/platform'
import { useApi } from '@/lib/api/client'

interface RootLayoutProps {
  slug: string
  tenantName?: string
  logoUrl?: string
}

export function RootLayout({ slug, tenantName, logoUrl }: RootLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const { t } = useTranslation('layout')
  const routerState = useRouterState()
  const breadcrumbs = buildBreadcrumbs(routerState.location.pathname, slug, t)

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      {/* Header — full width, 100px */}
      <Header slug={slug} tenantName={tenantName} logoUrl={logoUrl} />

      {/* Payment warning banner (admins only, past_due status) */}
      <PaymentWarningBanner />

      {/* Middle: sidebar + (breadcrumb bar + content) */}
      <div className="flex flex-1 overflow-hidden">
        <Sidebar
          slug={slug}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />

        {/* Content column with breadcrumb bar */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Breadcrumb bar */}
          <div className="flex items-center border-b px-6 py-2">
            <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm">
              {breadcrumbs.map((crumb, index) => (
                <span key={crumb.path} className="flex items-center gap-1">
                  {index > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
                  {index === breadcrumbs.length - 1 ? (
                    <span className="font-medium text-foreground">{crumb.label}</span>
                  ) : (
                    <span className="text-muted-foreground">{crumb.label}</span>
                  )}
                </span>
              ))}
            </nav>
          </div>

          {/* Content */}
          <main className="flex-1 overflow-y-auto p-6">
            <Outlet />
          </main>
        </div>
      </div>

      {/* Footer — full width */}
      <Footer />
    </div>
  )
}

// PaymentWarningBanner shows a dismissible warning when billing is past_due.
// Only visible to tenant admins. Reads from TanStack Query cache (no extra fetch).
function PaymentWarningBanner() {
  const { t } = useTranslation('platform')
  const billing = useSubscriptionStatus()
  const api = useApi()

  const portalMutation = useMutation({
    mutationFn: () => platformApi(api).createCustomerPortalSession(),
    onSuccess: (data) => {
      window.location.href = data.portal_url
    },
  })

  if (!billing || billing.status !== 'past_due') return null

  const band = billing.grace_period_band ?? 0

  const messageKey =
    band === 0
      ? 'subscription.billing.bannerBand0'
      : band === 1
        ? 'subscription.billing.bannerBand1'
        : 'subscription.billing.bannerBand2'

  const bgClass =
    band === 2
      ? 'bg-destructive text-destructive-foreground'
      : 'bg-amber-50 text-amber-900 border-b border-amber-200'

  return (
    <div className={`flex items-center justify-between gap-4 px-6 py-2 text-sm ${bgClass}`}>
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span>{t(messageKey)}</span>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="shrink-0 text-xs"
        onClick={() => portalMutation.mutate()}
        disabled={portalMutation.isPending}
      >
        {portalMutation.isPending
          ? t('subscription.billing.manageBillingOpening')
          : t('subscription.billing.manageBillingLink')}
      </Button>
    </div>
  )
}

interface Breadcrumb {
  label: string
  path: string
}

function buildBreadcrumbs(
  pathname: string,
  slug: string,
  t: (key: string) => string,
): Breadcrumb[] {
  const prefix = `/t/${slug}`
  const relativePath = pathname.startsWith(prefix) ? pathname.slice(prefix.length) : pathname

  const segments = relativePath.split('/').filter(Boolean)

  if (segments.length === 0) {
    return [{ label: t('breadcrumb.dashboard'), path: `${prefix}/dashboard` }]
  }

  const crumbs: Breadcrumb[] = []
  let currentPath = prefix

  for (const segment of segments) {
    currentPath = `${currentPath}/${segment}`
    const key = `breadcrumb.${segment}`
    const translated = t(key)
    const label = translated === key ? segment : translated
    crumbs.push({ label, path: currentPath })
  }

  return crumbs
}
